// Heuristic mapping: lifecycle stage_id → group gradient token.
//
// Used by stage-labels.ts as a fallback `tone_color` when neither the
// consolidated `specs/seeds/lifecycle/stage-labels.yaml` (CHG-1132 forward-promise)
// nor the per-domain seeds carry an explicit `tone_color`. Maps the stage's
// character (urgency, success, default) to one of the 10 group gradients
// declared in apps/dashboard-v2/src/styles/tokens.css.
//
// The gradient identity (10 named groups) is LOCKED for the v2 program — see
// specs/_product/dashboard-v2-tokens.md §3.5. This heuristic is deliberately
// limited to those 10 named tokens; never invents new ones.
//
// Per dashboard-v2-stage-treatments.md §4 (heuristic-fallback-table).

export type ToneToken =
  | "g-home"
  | "g-people"
  | "g-giving"
  | "g-events"
  | "g-channels"
  | "g-imports"
  | "g-automation"
  | "g-communications"
  | "g-settings"
  | "g-mygiving"
  // CHG-1523 — Platform admin nav group tone (slate→cyan). Mirrors
  // the GradientToken extension in nav.ts. Used by PlatformDashboard
  // StatStrip cards (CHG-1540).
  | "g-platform";

// Normalize input: 'at_risk' === 'at-risk'.
export function normalizeStageId(stage_id: string): string {
  return stage_id.replace(/-/g, "_");
}

// Heuristic table — first match wins, top to bottom.
// Each row: a normalized substring + the tone token to assign.
// Order matters: more specific stages first, generic-feeling ones last.
const RULES: Array<{ match: (s: string) => boolean; tone: ToneToken }> = [
  // Success / completion → giving (emerald-green; the "money in the door" group).
  { match: (s) => /^(complete|fulfilled|on_track|active)$/.test(s), tone: "g-giving" },

  // Urgent / danger → events (orange-amber; the "needs attention now" group).
  { match: (s) => /^(at_risk|at_risk_churn|off_pace|falling_behind|past_due|final_push)$/.test(s), tone: "g-events" },

  // Lost / canceled → people (rose-pink; the "donor relationship" group).
  { match: (s) => /^(lapsed|canceled|win_back_attempted)$/.test(s), tone: "g-people" },

  // New / launch → home (indigo-blue; the "first impression" group).
  { match: (s) => /^(launching|new|welcoming|prospect|first_time|committed)$/.test(s), tone: "g-home" },

  // Reactivation / mid-flight → automation (sky-blue; the "in motion" group).
  { match: (s) => /^(reactivated|fulfilling)$/.test(s), tone: "g-automation" },

  // VIP / loyalty → mygiving (pink-rose; the "donor portal" group, used for elevation).
  { match: (s) => /^(vip|long_standing|recurring)$/.test(s), tone: "g-mygiving" },

  // Post-close / archival → settings (zinc-gray; the "out of the loop" group).
  { match: (s) => /^(post_close)$/.test(s), tone: "g-settings" },
];

export function heuristicTone(stage_id: string): ToneToken {
  const norm = normalizeStageId(stage_id);
  for (const rule of RULES) {
    if (rule.match(norm)) return rule.tone;
  }
  return "g-settings";
}

// Severity tier for accessibility / sort order. Mirrors the heuristic above.
export type Severity = "info" | "success" | "warning" | "danger";

export function heuristicSeverity(stage_id: string): Severity {
  const norm = normalizeStageId(stage_id);
  if (/^(complete|fulfilled|on_track|active|long_standing|vip)$/.test(norm)) return "success";
  if (/^(at_risk|at_risk_churn|off_pace|falling_behind|final_push)$/.test(norm)) return "warning";
  if (/^(lapsed|canceled|past_due)$/.test(norm)) return "danger";
  return "info";
}
